import { Box, keyframes, styled, Typography } from "@mui/material";
import type { RenderStateCamera } from "@novorender/api";
import { Error as ErrorIcon } from "@procore/core-icons";
import { vec2 } from "gl-matrix";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { AsyncStatus } from "types/misc";
import { log } from "utils/log";

import { downloadDrawingData } from "./downloadDrawingData";
import { LargeMapWeb } from "./largeMapWeb";
import {
    clearNavigationPin,
    selectCurrentDrawing,
    selectDrawingPlacements,
    selectMiniMapDrawings,
    selectMiniMapLevels,
    selectNavigationPin,
    setCurrentDrawing,
} from "./minimapSlice";
import {
    calculateImageFitBox,
    drawPositionIndicator,
    modelToDrawingSpace,
    resolveSheetForCamera,
} from "./miniMapUtils";
import type { DrawingSpacePosition } from "./types";
import { useDrawingImage } from "./useDrawingImage";
import { useMiniMapData } from "./useMiniMapData";

const l = log.miniMap.miniMapWeb;

export const MINIMAP_WEB_ELEMENT_ID = "minimap-web-container";

const MiniMapContainer = styled(Box)({
    display: "flex",
    flexDirection: "column",
    gap: 10,
    alignItems: "flex-end",
    overflow: "visible",
    position: "absolute",
    bottom: 16,
    right: 16,
    pointerEvents: "auto",
});

const CanvasWrapper = styled(Box)({
    position: "relative",
    "&:hover .hover-layer": {
        borderColor: "rgba(255, 255, 255, 0.5)",
        boxShadow: "0px 0px 28px 0px rgba(0, 0, 0, 0.45)",
        opacity: 1,
    },
    "&:hover canvas": {
        boxShadow: "none",
    },
});

const HoverLayer = styled(Box)({
    position: "absolute",
    border: "4px solid transparent",
    borderRadius: 12,
    top: -3,
    right: -3,
    bottom: -3,
    left: -3,
    pointerEvents: "none",
    transition: "all 300ms ease-out",
    boxShadow: "0px 0px 0px 0px rgba(0, 0, 0, 0)",
    opacity: 1,
});

const StyledCanvas = styled("canvas", {
    shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled: boolean }>(({ disabled }) => ({
    display: "block",
    borderRadius: 8,
    boxShadow: "0px 0px 20px 0px rgba(0, 0, 0, 0.2)",
    transition: "box-shadow 300ms ease-out",
    backgroundColor: "white",
    cursor: disabled ? undefined : "pointer",
}));

const shimmerAnimation = keyframes`
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
`;

const ShimmerOverlay = styled(Box, {
    shouldForwardProp: (prop) => prop !== "visible",
})<{ visible: boolean }>(({ visible }) => ({
    position: "absolute",
    inset: 0,
    borderRadius: 8,
    overflow: "hidden",
    background: "linear-gradient(135deg, #a0a0a0, #d0d0d0)",
    opacity: visible ? 1 : 0,
    transition: "opacity 300ms ease-out",
    pointerEvents: "none",
    "&::after": {
        content: '""',
        position: "absolute",
        inset: 0,
        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
        animation: visible ? `${shimmerAnimation} 1.5s infinite` : "none",
    },
}));

const ErrorOverlay = styled(Box, {
    shouldForwardProp: (prop) => prop !== "visible",
})<{ visible: boolean }>(({ visible }) => ({
    position: "absolute",
    inset: 0,
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    background: "white",
    opacity: visible ? 1 : 0,
    transition: "opacity 300ms ease-out",
    pointerEvents: "none",
}));

interface MiniMapProps {
    className?: string;
}

// Longest-side cap for the mini map's decoded bitmap. The canvas is 240x172, so
// this comfortably covers high-DPR screens while keeping the decoded copy at a
// couple of MB instead of ~100MB.
const MINI_MAP_MAX_IMAGE_SIZE = 768;

function MiniMap({ onClick, largeMapOpen }: { onClick?: () => void; largeMapOpen: boolean }) {
    const { t } = useTranslation();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const currentDrawing = useAppSelector(selectCurrentDrawing);
    const levels = useAppSelector(selectMiniMapLevels);
    const drawings = useAppSelector(selectMiniMapDrawings);
    const placements = useAppSelector(selectDrawingPlacements);
    const navigationPin = useAppSelector(selectNavigationPin);
    const dispatch = useAppDispatch();
    // The web mini map fits the whole drawing into a ~240x172 canvas, so a
    // downscaled bitmap is visually identical while keeping only a tiny decoded
    // copy resident. While the large map is open it fully covers the mini map, so
    // release the bitmap (pass null) — only the large map's full-resolution
    // decode is resident then, never both. It re-decodes (cheaply) on close.
    const img = useDrawingImage(largeMapOpen ? null : currentDrawing, MINI_MAP_MAX_IMAGE_SIZE);
    const {
        state: { view, internalEventEmitter },
    } = useExplorerGlobals();

    const [drawingPosition, setDrawingPosition] = useState<DrawingSpacePosition>({
        position: vec2.fromValues(0, 0),
        rotation: 0,
    });

    const canvasWidth = 240;
    const canvasHeight = 172;

    const drawMap = useCallback(
        (
            ctx: CanvasRenderingContext2D,
            img: ImageBitmap,
            canvasWidth: number,
            canvasHeight: number,
            drawingPosition: DrawingSpacePosition,
        ) => {
            // A detached (closed) ImageBitmap reports width 0. A draw driven by
            // a closure/animation frame captured before `useDrawingImage`
            // released the bitmap can receive a stale, detached image; drawing
            // it throws "image source is detached".
            if (img.width === 0) {
                return;
            }
            ctx.clearRect(0, 0, canvasWidth, canvasHeight);
            const { width, height, x, y } = calculateImageFitBox({
                imageDimensions: [img.width, img.height],
                canvasDimensions: [canvasWidth, canvasHeight],
            });
            const positionX = drawingPosition.position[0] * width + x;
            const positionY = drawingPosition.position[1] * height + y;
            const rotation = drawingPosition.rotation;
            ctx.drawImage(img, x, y, width, height);
            drawPositionIndicator(ctx, positionX, positionY, rotation);
        },
        [],
    );

    useEffect(() => {
        if (!view || !internalEventEmitter || levels.length === 0) {
            return;
        }

        const handleCameraUpdated = (camera: RenderStateCamera) => {
            const { drawingId: targetDrawingId, releasePin } = resolveSheetForCamera({
                camera,
                levels,
                placements,
                currentDrawingId: currentDrawing?.id,
                navigationPin,
            });

            if (releasePin) {
                dispatch(clearNavigationPin());
            }

            if (targetDrawingId && targetDrawingId !== currentDrawing?.id) {
                l.debug(`Updating drawing to match camera position. From ${currentDrawing?.id} to ${targetDrawingId}`);
                dispatch(setCurrentDrawing(targetDrawingId));

                // Fetch the drawing data for the new drawing if we don't have it already
                const targetDrawing = drawings[targetDrawingId];
                if (!targetDrawing) {
                    l.error("Target drawing not found in state for id:", targetDrawingId);
                } else {
                    const { status } = targetDrawing.drawingData.previewAsyncState;
                    if (status === AsyncStatus.Initial) {
                        l.debug("Fetching drawing data for new drawing:", targetDrawing);
                        downloadDrawingData({
                            dispatch,
                            drawingId: targetDrawing.id,
                            url: targetDrawing.drawingData.url,
                            telemetrySource: "miniMap.miniMapWeb.fetchDrawingData",
                        }).then(() => {
                            l.debug("Finished downloading drawing data for mini map:", targetDrawing.id);
                        });
                    } else {
                        l.debug("Drawing data for target drawing already in state, no need to fetch:", targetDrawing);
                    }
                }
            }

            if (currentDrawing?.modelAB && currentDrawing?.drawingAB) {
                const result = modelToDrawingSpace(currentDrawing.modelAB, currentDrawing.drawingAB, camera);
                setDrawingPosition(result);
            }
        };

        handleCameraUpdated(view.renderState.camera);

        const unsubscribe = internalEventEmitter.on("cameraUpdated", handleCameraUpdated);

        return () => {
            unsubscribe();
        };
    }, [view, internalEventEmitter, levels, placements, navigationPin, currentDrawing, dispatch, drawings]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        const ctx = canvas.getContext("2d");
        if (!ctx) {
            return;
        }

        const dpr = window.devicePixelRatio || 1;

        canvas.width = canvasWidth * dpr;
        canvas.height = canvasHeight * dpr;
        canvas.style.width = `${canvasWidth}px`;
        canvas.style.height = `${canvasHeight}px`;
        ctx.scale(dpr, dpr);

        if (!currentDrawing) {
            l.debug("No current drawing selected for mini map, drawing placeholder");
            drawBackground(ctx, canvasWidth, canvasHeight);
            return;
        }

        // The mini map only consumes the preview, so its render is driven by the
        // preview state, not the (large-map-only) tiles state.
        const { status } = currentDrawing.drawingData.previewAsyncState;
        l.debug(
            "Drawing mini map, currentDrawing:",
            currentDrawing.id,
            "preview state:",
            currentDrawing.drawingData.previewAsyncState,
        );
        switch (status) {
            case AsyncStatus.Initial:
            case AsyncStatus.Loading:
                drawBackground(ctx, canvasWidth, canvasHeight);
                break;
            case AsyncStatus.Success:
                if (img) {
                    drawMap(ctx, img, canvasWidth, canvasHeight, drawingPosition);
                } else {
                    // Preview downloaded but not decoded yet; show placeholder
                    // until `useDrawingImage` resolves the bitmap.
                    drawBackground(ctx, canvasWidth, canvasHeight);
                }
                break;
            case AsyncStatus.Error:
            default:
                drawBackground(ctx, canvasWidth, canvasHeight);
        }
    }, [currentDrawing, drawingPosition, canvasWidth, canvasHeight, drawMap, img]);

    const status = currentDrawing?.drawingData.previewAsyncState.status;
    const isLoading = !currentDrawing || status === AsyncStatus.Initial || status === AsyncStatus.Loading;
    const isError = status === AsyncStatus.Error;
    const disabled = status !== AsyncStatus.Success;

    return (
        <Box
            className="minimap-inner"
            sx={{
                position: "relative",
                width: canvasWidth,
                height: canvasHeight,
            }}
        >
            <StyledCanvas ref={canvasRef} onClick={disabled ? undefined : onClick} disabled={disabled} />
            <ShimmerOverlay visible={isLoading} role="status" aria-live="polite" aria-label={t("loadingDrawing")} />
            <ErrorOverlay visible={isError} role="alert" aria-live="polite">
                <Box aria-hidden="true" sx={{ display: "flex" }}>
                    <ErrorIcon size="lg" />
                </Box>
                <Typography variant="body2">{t("loadingDrawingError")}</Typography>
            </ErrorOverlay>
        </Box>
    );
}

function drawBackground(ctx: CanvasRenderingContext2D, width: number, height: number) {
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "#a0a0a0");
    gradient.addColorStop(1, "#d0d0d0");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
}

export function MiniMapWeb({ className }: MiniMapProps) {
    const [isSheetOpen, setIsSheetOpen] = useState(false);

    const handleMiniMapClick = () => {
        setIsSheetOpen(true);
    };

    const handleSheetClose = () => {
        setIsSheetOpen(false);
    };

    useMiniMapData();

    const currentDrawing = useAppSelector(selectCurrentDrawing);

    if (currentDrawing === null) {
        return;
    }

    return (
        <>
            <MiniMapContainer id={MINIMAP_WEB_ELEMENT_ID} className={className}>
                <CanvasWrapper>
                    <HoverLayer className="hover-layer" />
                    <MiniMap onClick={handleMiniMapClick} largeMapOpen={isSheetOpen} />
                </CanvasWrapper>
            </MiniMapContainer>

            <LargeMapWeb open={isSheetOpen} onClose={handleSheetClose} />
        </>
    );
}
